import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Badge } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import useApi from '../hooks/useApi';

const CourseCard = ({ course, showProgress = false }) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const { createCheckoutSession } = useApi();

  const handleCardClick = () => {
    navigate(`/courses/${course.id}`);
  };

  const handleEnroll = async (e) => {
    e.stopPropagation();

    if (!isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: `/courses/${course.id}` } });
      return;
    }

    try {
      const session = await createCheckoutSession(course.id);
      const stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
      const { error } = await stripe.redirectToCheckout({
        sessionId: session.sessionId,
      });

      if (error) {
        console.error('Error:', error);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const formatPrice = (price, currency) => {
    if (price !== undefined && currency) {
      return `${currency} ${parseFloat(price).toFixed(2)}`;
    }
    return 'Price not available';
  };

  return (
    <div
      className="bg-white rounded-lg shadow-lg overflow-hidden transform transition duration-300 hover:scale-105 hover:shadow-xl cursor-pointer"
      onClick={handleCardClick}
    >
      <div className="relative">
        <img
          src={course.thumbnail || '/api/placeholder/400/300'}
          alt={course.title}
          className="w-full h-48 object-cover"
        />
        {course.isFeatured && (
          <div className="absolute top-0 right-0 bg-yellow-400 text-blue px-2 py-1 text-sm font-semibold">
            Featured
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-lg font-semibold mb-2 truncate">{course.title}</h3>
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
          {course.description}
        </p>
        {showProgress && course.progress !== undefined && (
          <div className="mb-4">
            <div className="flex justify-between mb-1">
              <span className="text-sm font-medium text-gray-700">
                Progress
              </span>
              <span className="text-sm font-medium text-gray-700">
                {course.progress}%
              </span>
              {course.completed && <Badge title="Completed" />}
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-blue-600 h-2.5 rounded-full"
                style={{ width: `${course.progress}%` }}
              ></div>
            </div>
          </div>
        )}

        {course.isEnrolled ? (
          <Link
            to={`/courses/${course.id}/content`}
            className="bg-green-600 text-white py-2 px-4 rounded-md flex items-center justify-center hover:bg-green-700 transition duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            Continue Learning
            <ChevronRight size={16} className="ml-1" />
          </Link>
        ) : (
          <button
            onClick={(e) => handleEnroll(e)}
            className="bg-blue-600 text-white py-2 px-4 rounded-md flex items-center justify-center hover:bg-blue-700 transition duration-300"
          >
            Enroll Now
            <ChevronRight size={16} className="ml-1" />
          </button>
        )}
      </div>

      <div className="flex justify-between items-center mt-4 p-4">
        <span className="text-lg font-bold text-green-600">
          {formatPrice(course.price, course.currency)}
        </span>
        <Link
          to={`/courses/${course.id}`}
          className="text-blue-600 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          View Course
        </Link>
      </div>
    </div>
  );
};

export default CourseCard;
